import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle, CheckCircle2, FileCheck2, FileText,
  Loader2, RefreshCw, TrendingUp, Upload,
} from "lucide-react";
import {
  Bar, CartesianGrid, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip, XAxis, YAxis,
} from "recharts";
import { toast } from "sonner";
import {
  getTaxHistory, importTaxDocuments, previewTaxDocuments, taxDocumentUrl,
} from "../lib/api";

const money = (value) => {
  if (value == null) return "—";
  const absolute = Math.abs(Number(value));
  if (absolute >= 1e7) return `₹${(Number(value) / 1e7).toFixed(2)}Cr`;
  if (absolute >= 1e5) return `₹${(Number(value) / 1e5).toFixed(2)}L`;
  return `₹${Number(value).toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
};

export default function TaxHistoryPage() {
  const [history, setHistory] = useState(null);
  const [loading, setLoading] = useState(true);
  const [files, setFiles] = useState([]);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getTaxHistory();
      setHistory(data);
    } catch {
      toast.error("Tax history could not be loaded");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const formData = () => {
    const form = new FormData();
    files.forEach(file => form.append("files", file));
    return form;
  };

  const previewFiles = async () => {
    if (!files.length) return;
    setBusy(true);
    try {
      const { data } = await previewTaxDocuments(formData());
      setPreview(data);
    } catch (error) {
      toast.error(error?.response?.data?.detail || "The PDFs could not be previewed");
    } finally {
      setBusy(false);
    }
  };

  const confirmImport = async () => {
    setBusy(true);
    try {
      const { data } = await importTaxDocuments(formData());
      toast.success(`${data.inserted} tax document${data.inserted === 1 ? "" : "s"} added`);
      setFiles([]);
      setPreview(null);
      await load();
    } catch (error) {
      toast.error(error?.response?.data?.detail || "The PDFs could not be imported");
    } finally {
      setBusy(false);
    }
  };

  const records = useMemo(() => history?.records || [], [history]);
  const latest = records[records.length - 1];
  const chartRows = useMemo(() => records.map(row => ({
    ...row,
    short_year: row.assessment_year.replace("20", ""),
  })), [records]);

  if (loading && !history) {
    return <div className="h-[65vh] flex items-center justify-center"><Loader2 className="animate-spin text-cyan-300" /></div>;
  }

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto space-y-5" data-testid="tax-history-page">
      <section className="rounded-2xl border border-emerald-400/15 bg-gradient-to-br from-emerald-400/[0.08] via-[var(--bg-surface)] to-cyan-400/[0.05] p-5 md:p-7">
        <div className="flex flex-wrap justify-between items-start gap-5">
          <div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-300 font-semibold flex items-center gap-2">
              <FileCheck2 size={14} /> Family financial memory
            </div>
            <h1 className="text-2xl md:text-3xl font-bold mt-2">Income Tax History</h1>
            <p className="text-sm text-[var(--text-secondary)] mt-2">
              Source-backed ITR acknowledgements, organized by assessment year.
            </p>
          </div>
          <button onClick={load} className="h-10 px-3 rounded-lg border border-white/10 text-sm flex items-center gap-2 hover:bg-white/5">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
      </section>

      <section className="grid grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi label="Years on record" value={history?.summary?.year_count || 0} tone="cyan" />
        <Kpi label="Latest total income" value={money(latest?.total_income)} tone="green" />
        <Kpi label="Latest taxes paid" value={money(latest?.total_taxes_paid)} tone="red" />
        <Kpi label="Latest effective rate" value={latest?.effective_tax_rate_pct != null ? `${latest.effective_tax_rate_pct}%` : "—"} tone="amber" />
      </section>

      {(history?.missing_assessment_years || []).length > 0 && (
        <section className="rounded-xl border border-amber-400/20 bg-amber-400/[0.05] p-4 flex gap-3">
          <AlertTriangle size={17} className="text-amber-300 shrink-0 mt-0.5" />
          <div className="text-sm">
            <span className="font-semibold">Missing acknowledgements:</span>
            <span className="text-[var(--text-secondary)] ml-2">{history.missing_assessment_years.join(", ")}</span>
            <p className="text-xs text-[var(--text-muted)] mt-1">Missing years are gaps, not zero income or tax.</p>
          </div>
        </section>
      )}

      <section className="rounded-xl border border-white/10 bg-[var(--bg-surface)] overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10">
          <h2 className="font-semibold flex items-center gap-2"><TrendingUp size={16} className="text-cyan-300" /> Income and tax progression</h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">Bars use assessment years; financial year is one year earlier.</p>
        </div>
        <div className="h-[410px] p-3 md:p-5">
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={chartRows} margin={{ top: 15, right: 30, left: 5, bottom: 15 }}>
              <CartesianGrid vertical={false} stroke="rgba(255,255,255,.08)" />
              <XAxis dataKey="assessment_year" tick={{ fill: "#8291aa", fontSize: 10 }} angle={-35} textAnchor="end" height={60} />
              <YAxis yAxisId="money" tickFormatter={value => `₹${(value / 1e5).toFixed(0)}L`} tick={{ fill: "#8291aa", fontSize: 10 }} width={55} />
              <YAxis yAxisId="rate" orientation="right" domain={[0, "auto"]} tickFormatter={value => `${value}%`} tick={{ fill: "#8291aa", fontSize: 10 }} width={42} />
              <Tooltip content={<TaxTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar yAxisId="money" dataKey="total_income" name="Total income" fill="#22d3ee" radius={[4, 4, 0, 0]} />
              <Bar yAxisId="money" dataKey="total_taxes_paid" name="Taxes paid" fill="#34d399" radius={[4, 4, 0, 0]} />
              <Line yAxisId="rate" dataKey="effective_tax_rate_pct" name="Effective tax rate" stroke="#fbbf24" strokeWidth={2.5} dot={{ r: 3 }} />
            </ComposedChart>
          </ResponsiveContainer>
        </div>
      </section>

      <section className="rounded-xl border border-white/10 bg-[var(--bg-surface)] overflow-hidden">
        <div className="px-5 py-4 border-b border-white/10">
          <h2 className="font-semibold">Year-by-year record</h2>
          <p className="text-xs text-[var(--text-muted)] mt-1">The active revised return is used when more than one version exists.</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[1050px] text-xs">
            <thead className="text-[var(--text-muted)] uppercase tracking-wider">
              <tr className="border-b border-white/10">
                <th className="text-left px-4 py-3">Assessment year</th>
                <th className="text-left">Financial year</th>
                <th className="text-right">Total income</th>
                <th className="text-right">Tax liability</th>
                <th className="text-right">Taxes paid</th>
                <th className="text-right">Refund</th>
                <th className="text-right">Effective rate</th>
                <th className="text-left px-4">Source</th>
              </tr>
            </thead>
            <tbody>
              {records.map(row => (
                <tr key={row.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-semibold">{row.assessment_year}{row.return_type === "revised" && <span className="ml-2 text-[9px] text-violet-300 uppercase">Revised</span>}</td>
                  <td className="text-[var(--text-secondary)]">{row.financial_year}</td>
                  <td className="text-right font-mono">{money(row.total_income)}</td>
                  <td className="text-right font-mono">{money(row.total_tax_liability)}</td>
                  <td className="text-right font-mono text-emerald-300">{money(row.total_taxes_paid)}</td>
                  <td className="text-right font-mono">{money(row.refund)}</td>
                  <td className="text-right font-mono">{row.effective_tax_rate_pct != null ? `${row.effective_tax_rate_pct}%` : "—"}</td>
                  <td className="px-4">
                    <a href={taxDocumentUrl(row.id)} target="_blank" rel="noreferrer" className="text-cyan-300 hover:text-cyan-200 flex items-center gap-1">
                      <FileText size={12} /> View PDF
                    </a>
                    {row.version_count > 1 && <div className="text-[9px] text-[var(--text-muted)] mt-1">{row.version_count} versions preserved</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="rounded-xl border border-white/10 bg-[var(--bg-surface)] p-5">
        <div className="flex items-center gap-2"><Upload size={16} className="text-cyan-300" /><h2 className="font-semibold">Add future acknowledgements</h2></div>
        <p className="text-xs text-[var(--text-muted)] mt-1">Select one or many one-page ITR acknowledgement PDFs. Every file is previewed and deduplicated first.</p>
        <input
          type="file" accept="application/pdf,.pdf" multiple
          onChange={event => { setFiles(Array.from(event.target.files || [])); setPreview(null); }}
          className="mt-4 block w-full text-sm text-[var(--text-secondary)] file:mr-4 file:rounded-md file:border-0 file:bg-cyan-400/10 file:px-4 file:py-2 file:text-cyan-200"
        />
        <div className="mt-4 flex gap-2">
          <button disabled={!files.length || busy} onClick={previewFiles} className="h-10 px-4 rounded-lg border border-cyan-400/25 text-cyan-200 disabled:opacity-40">
            {busy ? "Reading…" : `Preview ${files.length || ""} PDF${files.length === 1 ? "" : "s"}`}
          </button>
          {preview && <button disabled={busy || preview.new_count === 0} onClick={confirmImport} className="h-10 px-4 rounded-lg bg-emerald-400 text-slate-950 font-semibold disabled:opacity-40">Confirm import</button>}
        </div>
        {preview && (
          <div className="mt-4 rounded-lg border border-white/10 overflow-hidden">
            <div className="p-3 bg-white/[0.025] text-xs flex gap-4">
              <span className="text-emerald-300">{preview.new_count} new</span>
              <span className="text-[var(--text-muted)]">{preview.existing_count} already saved</span>
            </div>
            {preview.documents.map(row => (
              <div key={row.source.file_sha256} className="px-3 py-2 border-t border-white/5 text-xs flex justify-between gap-3">
                <span>{row.assessment_year} · {row.form_number || "ITR"} · {row.return_type}</span>
                <span className={row.warnings.length ? "text-amber-300" : "text-emerald-300"}>{row.warnings.length ? "Review" : <CheckCircle2 size={13} />}</span>
              </div>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

function Kpi({ label, value, tone = "cyan" }) {
  return (
    <div data-tone={tone} className="semantic-card rounded-xl border p-4">
      <div className="text-[9px] uppercase tracking-[0.16em] text-[var(--text-muted)]">{label}</div>
      <div className="semantic-value font-mono text-xl md:text-2xl mt-2">{value}</div>
    </div>
  );
}

function TaxTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const row = payload[0].payload;
  return (
    <div className="bg-[#0d1218] border border-white/15 rounded-md p-3 shadow-xl text-xs">
      <div className="font-semibold">{row.assessment_year}</div>
      <div className="text-[var(--text-muted)] mt-0.5">FY {row.financial_year}</div>
      <div className="mt-2 space-y-1">
        <div>Total income: <span className="font-mono text-cyan-300">{money(row.total_income)}</span></div>
        <div>Taxes paid: <span className="font-mono text-emerald-300">{money(row.total_taxes_paid)}</span></div>
        <div>Effective rate: <span className="font-mono text-amber-300">{row.effective_tax_rate_pct ?? "—"}%</span></div>
      </div>
    </div>
  );
}
